import { Icon } from '@iconify/react';
import { useState } from 'react';
import { api } from '../api';
import { getLang, t } from '../i18n';

/**
 * Tap-to-translate. The product is English-standard: nothing is translated
 * ahead of time. When the reader struggles, one tap translates this text to
 * her app language — and only then does the server spend a model call
 * (cached by content hash, so the same text is never paid for twice).
 *
 * Hidden when the reader's language is already English: there is nothing
 * to translate. Renders inline — the translated line appears under the
 * original, never replacing it, so a mistranslation can always be checked
 * against the source.
 */
export function TranslateButton({ text, className }: { text: string; className?: string }) {
  const lang = getLang();
  const [state, setState] = useState<
    { kind: 'idle' } | { kind: 'busy' } | { kind: 'done'; translation: string } | { kind: 'error' }
  >({ kind: 'idle' });
  const [open, setOpen] = useState(false);

  if (lang === 'en' || !text.trim()) return null;

  async function translate() {
    if (state.kind === 'busy') return;
    if (state.kind === 'done') {
      setOpen(!open);
      return;
    }
    setState({ kind: 'busy' });
    try {
      const res = await api<{ translations: string[] }>('/v1/translate', {
        method: 'POST',
        body: JSON.stringify({ texts: [text], target_lang: lang }),
      });
      const translation = res.translations[0];
      if (!translation) throw new Error('empty translation');
      setState({ kind: 'done', translation });
      setOpen(true);
    } catch {
      setState({ kind: 'error' });
    }
  }

  return (
    <span className={className}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void translate();
        }}
        aria-label={t('Translate')}
        className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-bold text-muted-foreground transition-all active:scale-95"
      >
        <Icon
          icon={state.kind === 'busy' ? 'ph:circle-notch-bold' : 'solar:translation-bold'}
          className={state.kind === 'busy' ? 'animate-spin' : ''}
        />
        {state.kind === 'done' && open ? t('Original') : t('Translate')}
      </button>
      {state.kind === 'done' && open && (
        <span dir="auto" className="mt-1 block text-[13px] leading-snug text-muted-foreground">
          {state.translation}
        </span>
      )}
      {state.kind === 'error' && (
        <span className="mt-1 block text-[11px] text-urgent">
          {t('Could not translate right now')}
        </span>
      )}
    </span>
  );
}
